// Which installed devices can be controlled over Bluetooth.
//
// ESP8266 (OtaForTuya and the other sketches) has NO BLE, so the offline-BLE
// path must never engage for it. The backend marks the capable ones per device
// with DevicesInstalled.BLEAvailable (0 for the whole current fleet); the app
// reads that flag off the device-list rows and records it here, so the routing
// in core/utils/deviceController can ask `isBleCapable(uno)` synchronously
// inside a tap handler.
//
// ⚠️ ADDITIVE, AND NEVER CLEARED BY A ROW THAT OMITS THE COLUMN. An older
// backend (before ble_available_migration.sql) returns no BLEAvailable at all —
// that is "unknown", not "false", and must not unset a flag another row set.
// A row that DOES carry the column is authoritative, both ways. The whole set
// starts empty, so until a row proves a device capable, BLE is off — which is
// exactly today's behaviour for every device in the field.
//
// Same shape as core/ble/bleForced (a plain in-memory registry, not persisted);
// it is rebuilt from the device lists every session.

const capable = new Set();

const key = (uno) => String(uno ?? '').trim();

// 1 / '1' / true = capable; 0 / '0' / false = not. Anything else (null,
// undefined, missing) leaves the current knowledge untouched.
function readFlag(v) {
  if (v === 1 || v === '1' || v === true) return true;
  if (v === 0 || v === '0' || v === false) return false;
  return null; // unknown — do not change what we know
}

/** Record capability from one device row or a list of them. */
export function noteBleCapable(rowOrRows) {
  const rows = Array.isArray(rowOrRows) ? rowOrRows : [rowOrRows];
  rows.forEach((r) => {
    const k = key(r && r.UniqueNumber);
    if (!k) return;
    const flag = readFlag(r.BLEAvailable);
    if (flag === true) capable.add(k);
    else if (flag === false) capable.delete(k);
    // flag === null: row didn't carry the column — leave as is.
  });
}

/** Is this device flagged BLE-capable? False until a row proves otherwise. */
export const isBleCapable = (uno) => capable.has(key(uno));

// Testing / sign-out: forget everything learned this session.
export function resetBleCapable() {
  capable.clear();
}
